import React, {useCallback, useEffect, useState} from 'react';
import {Autocomplete, Box, CircularProgress, TextField, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {fetchHeaders} from '../../../utils/headers';
import Constants from '../../../utils/constants';
import {Search} from '@mui/icons-material';

export default function ConteneurSearchBar({clientSelected}) {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const searchConteneurs = useCallback((query) => {
        if (!clientSelected || query.trim().length < 2) {
            setOptions([]);
            return;
        }
        setLoading(true);
        const url = `${Constants.API_ROUTE_CONTENEURS}/search?client=${clientSelected}&q=${encodeURIComponent(query)}`;
        fetch(url, fetchHeaders())
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setOptions(data);
                } else {
                    setOptions([]);
                }
            })
            .catch(() => setOptions([]))
            .finally(() => setLoading(false));
    }, [clientSelected]);

    useEffect(() => {
        if (inputValue.trim().length < 2) {
            setOptions([]);
            return;
        }
        const timer = setTimeout(() => {
            searchConteneurs(inputValue);
        }, 350);
        return () => clearTimeout(timer);
    }, [inputValue, searchConteneurs]);

    const handleSelect = (event, value) => {
        if (value && value.id) {
            navigate(Constants.APP_ROUTE_INTERVENTIONS.replace(':id', value.id));
        }
    };

    return (
        <Autocomplete
            freeSolo
            filterOptions={(x) => x}
            options={options}
            loading={loading}
            inputValue={inputValue}
            onInputChange={(event, newValue) => setInputValue(newValue)}
            onChange={handleSelect}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.nomenclature} – ${option.adresse}, ${option.commune}`
            }
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Box>
                        <Typography variant="body2" fontWeight="bold">{option.nomenclature} - {option.patrimoine.libelle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {option.adresse}, {option.commune}
                            {option.patrimoine?.libelle && ` · `}
                        </Typography>
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Rechercher un conteneur (nomenclature, adresse…)"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search fontSize="small" sx={{mr: 1, color: 'text.secondary'}}/>,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={16}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            noOptionsText={
                inputValue.trim().length < 2
                    ? 'Saisissez au moins 2 caractères'
                    : 'Aucun conteneur trouvé'
            }
            sx={{minWidth: 380}}
        />
    );
}
